import { useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { createSearchParams, Link, useNavigate } from 'react-router-dom';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { SupplierAttributeType } from '@prisma/client';
import { Breadcrumb, Button, Dropdown, Input, Layout, MenuProps, Space, Tag } from 'antd';
import Table, { ColumnsType } from 'antd/es/table';

import { Role, SupplierAttributeDetail } from '@app/domain-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { Auth, ConfirmDelete, PageLayout, Tables, Theme } from '@app/web-components';

import { CreatedByColumn, UpdatedByColumn } from '../../../../common';
import { useDeleteSupplierAttribute, useSupplierAttributes } from '../data';

export const TITLE = 'Supplier Attribute Mapping';

export const LandingPage = () => {
  const { user } = Auth.useUserAuth();

  const { getColor } = Theme.useIntelAccentColors();
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const handleActionMenuClick = (e: { key: string }, record: SupplierAttributeDetail) => {
      switch (e.key) {
        case 'create':
          navigate({
            pathname: `./create`,
            search: createSearchParams({
              id: record.supplier.externalId.toString(),
              name: record.supplier.name,
            }).toString(),
          });
          break;
        case 'delete':
          setDeleteTarget(record);
          break;
        default:
          break;
      }
    };

    const actionItems: MenuProps['items'] = [
      {
        label: 'Create New',
        key: 'create',
        icon: <PlusOutlined />,
      },
      {
        label: 'Delete',
        key: 'delete',
        icon: <DeleteOutlined />,
        danger: true,
      },
    ];

    const _columns: ColumnsType<SupplierAttributeDetail> = [
      {
        title: 'Action',
        width: 100,
        render: (_, record) => (
          <Dropdown.Button
            size="small"
            onClick={() => navigate(`./${record.id}`)}
            menu={{ items: actionItems, onClick: (e) => handleActionMenuClick(e, record) }}
          >
            <Space>
              <EditOutlined />
              Edit
            </Space>
          </Dropdown.Button>
        ),
      },
      {
        title: 'Supplier Id',
        dataIndex: ['supplier', 'externalId'],
        width: 120,
        ellipsis: true,
        responsive: ['lg'],
        sorter: true,
      },
      {
        title: 'Supplier Name',
        dataIndex: ['supplier', 'name'],
        ellipsis: true,
        responsive: ['lg'],
        sorter: true,
      },
      {
        title: 'Attribute Name',
        dataIndex: ['attribute'],
        ellipsis: true,
        sorter: true,
      },
      {
        title: 'Attribute Type',
        dataIndex: ['attributeType'],
        width: 140,
        ellipsis: true,
        sorter: true,
        render: (_, { attributeType }) => {
          const [bgColor, txtColor] = getColor(attributeType);
          return attributeType ? (
            <Tag key={attributeType} style={{ color: txtColor }} color={bgColor}>
              {attributeType === SupplierAttributeType.DMS_NAME ? 'DMS' : 'T3'}
            </Tag>
          ) : undefined;
        },
      },
      { ...UpdatedByColumn, responsive: ['xl'] },
      {
        title: 'Updated On',
        dataIndex: 'updatedOn',
        width: 180,
        ellipsis: true,
        responsive: ['xl'],
        render: (date: string) => formatIsoDate(date),
        sorter: true,
      },
      { ...CreatedByColumn, responsive: ['xxl'] },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        width: 180,
        ellipsis: true,
        render: (date: string) => formatIsoDate(date),
        sorter: true,
        defaultSortOrder: 'descend',
      },
    ];

    return _columns;
  }, [getColor, navigate]);

  const {
    getCreateButtonProps,
    getCSVLinkProps,
    getParentLayoutContentProps,
    getQuery,
    getReloadButtonProps,
    getResetFiltersButtonProps,
    getSearchInputProps,
    getTableProps,
    getToolbarProps,
  } = Tables.useTableState<SupplierAttributeDetail>('Supplier Attribute', columns, {
    globalSearchFields: ['supplier.name', 'attribute'],
  });

  const queryResult = useSupplierAttributes(
    getQuery({
      include: { supplier: true, updatedByUser: true, createdByUser: true },
    }),
  );
  const del = useDeleteSupplierAttribute();
  const [deleteTarget, setDeleteTarget] = useState<SupplierAttributeDetail | undefined>(undefined);

  return (
    <>
      <ConfirmDelete.Modal
        itemId={deleteTarget?.id}
        itemName={deleteTarget?.attribute}
        onClose={() => setDeleteTarget(undefined)}
        onOk={del.mutate}
      />
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: <Link to="/">Home</Link> }, { title: 'Admin' }, { title: 'Maps' }, { title: TITLE }]}
      />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title={TITLE} />
        <div
          {...getToolbarProps({
            items: [
              <Input {...getSearchInputProps()} />,
              user?.roles[Role.RULE_ADMIN] ? <Button {...getCreateButtonProps()} /> : undefined,
              <Button {...getReloadButtonProps()} onClick={() => queryResult.refetch()} />,
              <Button {...getResetFiltersButtonProps()} />,
              <CSVLink {...getCSVLinkProps(queryResult.data?.data)} />,
            ],
          })}
        />
        <Table {...getTableProps(queryResult)} />
      </Layout.Content>
    </>
  );
};
